'use client'
import { useLocale } from 'next-intl'
import { setLocale } from '@/i18n/locale'
import type { Locale } from '@/i18n/config'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Check, ChevronDown } from 'lucide-react'
import { useParams, usePathname, useRouter } from 'next/navigation'
import { LOCALE_COOKIE_NAME } from '@/i18n/constants'
import { cn } from '@/lib/utils'

export const LanguageSelector = ({
  variant = 'dark',
}: {
  variant?: 'light' | 'dark'
}) => {
  const locale = useLocale()
  const params = useParams()
  const pathname = usePathname()
  const router = useRouter()

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  ]

  function onChangeLang(newLocale: Locale) {
    const langFromUrl = Array.isArray(params.lang)
      ? params.lang[0]
      : params.lang
    const isUrlBasedPage =
      pathname.includes('/changelog') || pathname.includes('/feature')

    if (isUrlBasedPage) {
      // This is a page that uses URL-based localization
      const currentLocaleInPath = langFromUrl || 'en'
      if (currentLocaleInPath === newLocale) {
        return // No change needed
      }

      let basePath = pathname
      if (langFromUrl) {
        // Strip the existing locale from the path
        basePath = pathname.substring(langFromUrl.length + 1) // +1 for the slash
      }

      // Handle the special case for 'en', which should not have a prefix
      const newPath = newLocale === 'en' ? basePath : `/${newLocale}${basePath}`
      router.push(newPath)
      router.refresh()
    } else {
      // This is a page that uses client-side localization
      if (newLocale !== locale) {
        // Set cookie on client side first to ensure it's immediately available
        if (typeof document !== 'undefined') {
          document.cookie = `${LOCALE_COOKIE_NAME}=${newLocale}; path=/; max-age=31536000; SameSite=Lax`
          // Also set on server side for persistence
          setLocale(newLocale).then(() => {
            // Use window.location.reload() instead of router.refresh() to ensure
            // the cookie is included in the request headers. This prevents the flash
            // of wrong language that can happen with router.refresh() due to async timing.
            window.location.reload()
          })
        } else {
          setLocale(newLocale)
          router.refresh()
        }
      }
    }
  }

  const currentLanguage = languages.find((lang) => lang.code === locale)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'text-xs sm:text-sm p-1 sm:p-4 h-6 sm:h-10 rounded-lg',
            variant === 'light'
              ? 'text-black hover:bg-black/5 hover:text-black border-black/20'
              : 'text-white/60 hover:bg-white/10 hover:text-white border-white/20',
          )}
        >
          <span className="flex items-center gap-1">
            <span className="text-sm sm:text-base">
              {currentLanguage?.flag}
            </span>
            <span className="hidden sm:block text-xs sm:text-sm">
              {currentLanguage?.name}
            </span>
          </span>
          <ChevronDown className="h-2.5 w-2.5 sm:h-3 sm:w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[180px] bg-cyber-black text-white"
      >
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => onChangeLang(lang.code as Locale)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </span>
            {locale === lang.code && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
